import { useState } from 'react';
import Autocomplete from './Autocomplete';
import logger from '../../utils/logger';
import { BoxProps } from '@mui/material';
import { CategorySuggestion, EquipmentClassSuggestion, SearchSuggetion } from '../../types/search';
import { getSearchAutoSuggestOptions } from '../../apis/search';
import { getSiteSearchSuggestions } from '../../apis/site-search';
import { Subject } from 'rxjs';
import { v4 as uuid } from 'uuid';
import {
  AutoSuggestSearchOptionType,
  AutocompleteOption,
  AutocompleteOptionGroup,
  AutocompleteOptionType,
  AutocompletePopupPosition,
  SearchAutoSuggestOption,
  AutocompleteSelectableOption,
} from '../../types/autocomplete';
import {
  DEFAULT_AUTOSUGGEST_RESULT_SIZE,
  SEARCH_DEFAULT_DEBOUNCE_TIME,
} from '../../constants/search';
import {
  redirectToCategoryPage,
  redirectToEquipmentClass,
  redirectToTextSearchResultsPage,
} from '../../utils/router';
import {
  getSiteSearchItemDisplaySubtitle,
  getSiteSearchItemDisplayTitle,
  getSiteSearchItemLink,
  markHighlightToStrongHtml,
} from '../../utils/site-search';
import type { SiteSearchItem, SiteSearchSuggestionsResponse } from '../../types/site-search';

const MAX_EQUIPMENT_SUGGESTIONS = 3;
const MAX_CATEGORY_SUGGESTIONS = 3;
const MAX_SITE_SUGGESTIONS = 6;

interface SearchAutoSuggestProps extends BoxProps {
  startIcon?: JSX.Element;
  endIcon?: JSX.Element;
  helperText?: JSX.Element;
  popupPosition?: AutocompletePopupPosition;
  placeholder?: string;
  inputFocusSubject?: Subject<boolean>;
  closeAutocompleteSubject?: Subject<boolean>;
  onDropdownToggleFn?: (popupState: boolean) => void;
  onInputChangeCallBack?: (value: string) => void;
}

function SearchAutoSuggest({
  startIcon,
  endIcon,
  popupPosition,
  placeholder,
  helperText,
  inputFocusSubject,
  closeAutocompleteSubject,
  onDropdownToggleFn,
  onInputChangeCallBack,
  ...rest
}: SearchAutoSuggestProps) {
  const getNormalizedGroupLabel = (rawLabel: string | null | undefined): string => {
    const trimmedLabel = typeof rawLabel === 'string' ? rawLabel.trim().toLowerCase() : '';
    return trimmedLabel || 'pages';
  };

  const getFirstNonEmptyValue = (items?: string[]): string | undefined => {
    return items?.find((item) => item && item.trim().length > 0)?.trim();
  };

  const getSiteSuggestionDisplayLabel = (siteSuggestion: SiteSearchItem): string => {
    const highlightedLabel =
      getFirstNonEmptyValue(siteSuggestion.highlight?.['page_title.suggest']) ??
      getFirstNonEmptyValue(siteSuggestion.highlight?.['content_title.suggest']);

    if (highlightedLabel) {
      return markHighlightToStrongHtml(highlightedLabel);
    }

    return getSiteSearchItemDisplayTitle(siteSuggestion);
  };

  const getSiteSuggestionGroups = (siteSuggestions: SiteSearchItem[]): AutocompleteOptionGroup[] => {
    const groupedMap = new Map<string, AutocompleteOption[]>();

    siteSuggestions.forEach((siteSuggestion) => {
      const title = getSiteSuggestionDisplayLabel(siteSuggestion);
      const url = getSiteSearchItemLink(siteSuggestion);
      if (!title || !url) {
        return;
      }

      const groupLabel = getNormalizedGroupLabel(getSiteSearchItemDisplaySubtitle(siteSuggestion));
      const existingGroupItems = groupedMap.get(groupLabel);
      const siteSuggestionItem: AutocompleteOption = {
        id: uuid(),
        label: title,
        type: AutocompleteOptionType.STANDARD,
        url,
        optionType: AutoSuggestSearchOptionType.SITEPAGE,
      };

      if (existingGroupItems) {
        existingGroupItems.push(siteSuggestionItem);
      } else {
        groupedMap.set(groupLabel, [siteSuggestionItem]);
      }
    });

    return Array.from(groupedMap.entries()).map(([groupLabel, groupItems]) => ({
      id: uuid(),
      groupLabel,
      groupItems,
    }));
  };

  const onEnterInInputField = (value: string) => {
    value = value.trim();
    if (value) {
      redirectToTextSearchResultsPage(value);
    }
  };

  const selectHandler = async (value: SearchAutoSuggestOption) => {
    if (value.optionType === AutoSuggestSearchOptionType.EQUIPMENTCLASS && value.slug) {
      redirectToEquipmentClass(value.slug);
    }
    if (value.optionType === AutoSuggestSearchOptionType.CATEGORIES && value.slug) {
      redirectToCategoryPage(value.slug);
    }
    if (value.optionType === AutoSuggestSearchOptionType.SITEPAGE && value.url) {
      window.location.href = value.url;
    }
  };

  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(null);

  const getOptions = async (value: string): Promise<AutocompleteOptionGroup[]> => {
    if (!value) {
      return [];
    }

    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
      setDebounceTimeout(null);
    }

    return new Promise<AutocompleteOptionGroup[]>((resolve) => {
      const newTimeout = setTimeout(async () => {
        let fetchedOptions: AutocompleteOptionGroup[] = [];

        try {
          const [autoSuggestResult, siteSearchResult] = await Promise.allSettled([
            getSearchAutoSuggestOptions(value, DEFAULT_AUTOSUGGEST_RESULT_SIZE),
            getSiteSearchSuggestions(value),
          ]);

          if (autoSuggestResult.status === 'rejected') {
            logger('error', 'Error fetching equipment/category suggestions', autoSuggestResult.reason);
          }

          if (siteSearchResult.status === 'rejected') {
            logger('error', 'Error fetching site suggestions', siteSearchResult.reason);
          }

          const fetchedApiData: SearchSuggetion =
            autoSuggestResult.status === 'fulfilled'
              ? autoSuggestResult.value
              : { equipment_suggestions: [], category_suggestions: [] };

          const fetchedSiteSearchData: SiteSearchSuggestionsResponse =
            siteSearchResult.status === 'fulfilled'
              ? siteSearchResult.value
              : { suggestions: [] };

          const hasNoEquipmentSuggestions = fetchedApiData.equipment_suggestions.length === 0;
          const hasNoCategorySuggestions = fetchedApiData.category_suggestions.length === 0;
          const hasNoSiteSuggestions = fetchedSiteSearchData.suggestions.length === 0;

          if (hasNoEquipmentSuggestions && hasNoCategorySuggestions && hasNoSiteSuggestions) {
            fetchedOptions = [];
          } else {
            const optionsList: AutocompleteOptionGroup[] = [];

            if (!hasNoEquipmentSuggestions) {
              const equipmentClassGroupItems: AutocompleteOption[] =
                fetchedApiData.equipment_suggestions
                  .slice(0, MAX_EQUIPMENT_SUGGESTIONS)
                  .map((e: EquipmentClassSuggestion) => ({
                    id: uuid(),
                    label: e.text,
                    type: AutocompleteOptionType.STANDARD,
                    slug: e.equipment_class_slug,
                    optionType: AutoSuggestSearchOptionType.EQUIPMENTCLASS,
                  }));

              optionsList.push({
                id: uuid(),
                groupLabel: 'equipment',
                groupItems: equipmentClassGroupItems,
              });
            }

            if (!hasNoCategorySuggestions) {
              const categoryGroupItems: AutocompleteOption[] =
                fetchedApiData.category_suggestions
                  .slice(0, MAX_CATEGORY_SUGGESTIONS)
                  .map((e: CategorySuggestion) => ({
                    id: uuid(),
                    label: e.text,
                    type: AutocompleteOptionType.STANDARD,
                    slug: e.category_slug,
                    optionType: AutoSuggestSearchOptionType.CATEGORIES,
                  }));

              optionsList.push({
                id: uuid(),
                groupLabel: 'categories',
                groupItems: categoryGroupItems,
              });
            }

            const siteSuggestionGroups = getSiteSuggestionGroups(
              fetchedSiteSearchData.suggestions.slice(0, MAX_SITE_SUGGESTIONS),
            );
            optionsList.push(...siteSuggestionGroups);

            fetchedOptions = optionsList;
          }
        } catch (error) {
          logger('error', 'Error fetching search suggestions', error);
        }

        resolve(fetchedOptions);

        // Set debounceTimeout back to null only after resolving the promise
        setDebounceTimeout(null);
      }, SEARCH_DEFAULT_DEBOUNCE_TIME);

      setDebounceTimeout(newTimeout);
    });
  };

  return (
    <Autocomplete
      value={null}
      startIcon={startIcon}
      endIcon={endIcon}
      selectHandler={async (selectedOption: AutocompleteSelectableOption) => {
        if (selectedOption !== null) {
          await selectHandler(selectedOption as SearchAutoSuggestOption);
        }
      }}
      getOptionsHandler={getOptions}
      isLoading={false}
      popupPosition={popupPosition ?? 'absolute'}
      placeholder={placeholder ?? ''}
      helperText={helperText}
      onDropdownToggleFn={onDropdownToggleFn}
      overRideOnEnterInInputField={onEnterInInputField}
      onClick={() => inputFocusSubject?.next(true)}
      inputFocusSubject={inputFocusSubject}
      closeAutocompleteSubject={closeAutocompleteSubject}
      onInputChangeCallBack={onInputChangeCallBack}
      {...rest}
    />
  );
}

export default SearchAutoSuggest;
